import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';
import { $$api } from '@doger/core/shared/api';

import styles from './apply-for-an-whitelist-form.module.scss';

type ApplyForAnWhitelistFormProps = {
  className?: string;
  onSuccess: () => void;
};

export const ApplyForAnWhitelistForm = ({
  className,
  onSuccess,
}: ApplyForAnWhitelistFormProps) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        industry: z
          .string()
          .nonempty(
            t('whitelist.application.form.fields.industry.errors.required')
          )
          .min(
            10,
            t('whitelist.application.form.fields.industry.errors.minLength', {
              replace: {
                min: 10,
              },
            })
          )
          .max(
            1024,
            t('whitelist.application.form.fields.industry.errors.maxLength', {
              replace: {
                max: 1024,
              },
            })
          ),
        contact: z
          .string()
          .nonempty(
            t('whitelist.application.form.fields.contact.errors.required')
          )
          .max(
            256,
            t('whitelist.application.form.fields.contact.errors.maxLength', {
              replace: {
                max: 256,
              },
            })
          ),
      }),
    [t]
  );

  const form = useAppForm({
    defaultValues: {
      industry: '',
      contact: '',
    },
    validators: {
      onSubmit: schema,
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      await applyMutation.mutateAsync({
        industry: value.industry,
        contact: value.contact,
      });

      await $$api.tokensService.refreshTokens().finally(() => {
        $$toast.success(t('whitelist.application.form.success'), {
          duration: 60_000,
        });
        onSuccess();
      });
    },
  });

  const applyMutation = useMutation({
    mutationKey: ['auth', 'apply-for-an-whitelist'],
    mutationFn: async (data: z.infer<typeof schema>) =>
      $$api.applyForAnWhitelist({
        industry: data.industry,
        contact: data.contact,
      }),

    onError: () => {
      $$toast.error(t('whitelist.application.form.errors.unknown'));
    },
  });

  return (
    <div className={className}>
      <form
        name='apply-for-an-whitelist-form'
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <div className={styles.fields}>
          <form.AppForm>
            <form.AppField
              name='contact'
              children={(field) => (
                <field.TextAreaField
                  label={t('whitelist.application.form.fields.contact.label')}
                  placeholder={t(
                    'whitelist.application.form.fields.contact.placeholder'
                  )}
                  autoComplete='email'
                  autoFocus
                />
              )}
            />
          </form.AppForm>

          <form.AppForm>
            <form.AppField
              name='industry'
              children={(field) => (
                <field.TextAreaField
                  label={t('whitelist.application.form.fields.industry.label')}
                  placeholder={t(
                    'whitelist.application.form.fields.industry.placeholder'
                  )}
                  rows={7}
                />
              )}
            />
          </form.AppForm>
        </div>

        <form.AppForm>
          <form.SubmitButton
            variant='contained'
            size='lg'
            palette='primary'
            children={({ isSubmitting }) =>
              isSubmitting
                ? t('whitelist.application.form.action.submitting')
                : t('whitelist.application.form.action.submit')
            }
          />
        </form.AppForm>
      </form>
    </div>
  );
};
